import { IconAdjustmentsHorizontal } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { CustomizeLayoutModal } from "./CustomizeLayoutModal";
import {
  HREF_REVISION_DOCUMENTAL,
  HREF_SOLICITUDES_CATALOGO,
  NAV_ADMIN_GRUPOS,
  NAV_PRINCIPAL,
  etiquetaNavItem,
  filtrarNavParaPermisos,
  type NavItem,
} from "@/components/layout/data/nav-items";
import { isNavActive } from "@/components/layout/utils/path-matching";
import {
  useMisTiposResponsableCatalogo,
  useSolicitudesCatalogoPendientes,
} from "@/features/bandeja-solicitudes-catalogo/hooks/use-bandeja-solicitudes-catalogo";
import { useRevisionPendientes } from "@/features/revision-documental/hooks/use-revision-documental";
import { cn } from "@/lib/cn";
import { Z_INDEX } from "@/lib/z-index";
import { Badge } from "../Badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip";
import { SectionLabel } from "./SectionLabel";

/**
 * Sidebar del AppShell · sección "Navegación" (siempre) + sección
 * "Administración" (solo si el usuario ve al menos 1 item admin).
 *
 * `nav-items.ts`/`path-matching.ts` (`components/layout/`) son datos y
 * lógica pura SIN dependencia de Mantine — se reusan tal cual, sin fork.
 *
 * Dos modos: expandido (ícono + etiqueta) y `colapsado` (rail · solo
 * ícono centrado + tooltip a la derecha). El ANCHO real (72px↔248px) lo
 * fija el `AppShell` orquestador vía la CSS var `--shell-navbar-w`; este
 * componente solo decide qué renderiza en cada modo.
 */
export function AppShellNavbar({
  roles,
  permisos,
  pathname,
  mobileOpen,
  onItemClick,
  colapsado = false,
  onMouseEnter,
  onMouseLeave,
}: {
  /** Roles del usuario · solo para las ETIQUETAS dependientes de rol (no gatea). */
  roles: ReadonlyArray<string>;
  /** Permisos efectivos · gatean la visibilidad de los items (RBAC). */
  permisos: ReadonlyArray<string>;
  pathname: string;
  mobileOpen: boolean;
  onItemClick: () => void;
  colapsado?: boolean;
  /** Hover · el AppShell expande al entrar y colapsa al salir (sidebar por hover). */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const navigate = useNavigate();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Bandeja de solicitudes de catálogo · visible si el usuario atiende algún tipo (por rol).
  const { data: tiposCatalogo } = useMisTiposResponsableCatalogo();
  const esResponsableCatalogo = (tiposCatalogo?.length ?? 0) > 0;
  // Bandeja de revisión documental · visible si el usuario revisa alguna categoría/tipo (por rol).
  const { data: revision } = useRevisionPendientes();
  const principal = filtrarNavParaPermisos(NAV_PRINCIPAL, permisos, esResponsableCatalogo, revision?.puedeRevisar ?? false);
  // Cada grupo de administración con sus items visibles según permisos · se ocultan
  // los grupos vacíos (ej. un Auditor solo ve "Auditoría" → solo el grupo Sistema).
  const adminGrupos = NAV_ADMIN_GRUPOS.map((g) => ({
    titulo: g.titulo,
    items: filtrarNavParaPermisos(g.items, permisos),
  })).filter((g) => g.items.length > 0);
  const hayAdmin = adminGrupos.length > 0;

  const { data: pendientesCatalogo } = useSolicitudesCatalogoPendientes(esResponsableCatalogo);

  function irA(href: string) {
    return () => {
      void navigate({ to: href as never });
      onItemClick();
    };
  }

  function renderItem(item: NavItem) {
    const activo = isNavActive(pathname, item.href);
    const label = etiquetaNavItem(item, roles);
    const conteo =
      item.href === HREF_SOLICITUDES_CATALOGO && (pendientesCatalogo ?? 0) > 0
        ? pendientesCatalogo
        : item.href === HREF_REVISION_DOCUMENTAL && (revision?.pendientes ?? 0) > 0
          ? revision?.pendientes
          : undefined;
    const Icon = item.icon;

    const linkClass = cn(
      // Alineación y padding CONSTANTES (sin justify-center al colapsar) · así el
      // icono queda a la izquierda en AMBOS estados y NO se desplaza horizontalmente
      // al expandir/contraer · solo el label aparece/desaparece a su derecha.
      "flex items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-2 text-[13px] font-medium transition-colors",
      activo
        ? "bg-secondary font-semibold text-foreground shadow-2xs"
        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
    );

    const link = (
      <button type="button" onClick={irA(item.href)} aria-label={label} className={cn(linkClass, "w-full")}>
        <Icon
          size={17}
          stroke={1.7}
          className={cn("shrink-0", activo ? "text-multiva-600" : "text-muted-foreground")}
        />
        {/* El label NO se desmonta al colapsar · su max-width + opacity se
            interpolan (con la MISMA duración direccional del ancho, --navbar-anim-ms,
            y el mismo easing expo) para que el texto se retraiga JUNTO con la barra,
            no que desaparezca antes de que se contraiga. */}
        <span
          className={cn(
            "block min-w-0 flex-1 truncate text-left transition-[max-width,opacity] ease-[cubic-bezier(0.08,1,0.12,1)] duration-[var(--navbar-anim-ms,720ms)]",
            colapsado ? "max-w-0 opacity-0" : "max-w-[180px] opacity-100"
          )}
        >
          {label}
        </span>
        {!colapsado && conteo !== undefined && (
          <Badge size="xs" variant="filled" color="multiva" className="shrink-0 rounded-full">
            {conteo}
          </Badge>
        )}
      </button>
    );

    // Se envuelve SIEMPRE en Tooltip (estructura estable · si se cambiara el árbol
    // entre colapsado/expandido, el botón se remontaría y el label perdería su
    // transición). El tooltip lateral solo se muestra en modo rail.
    return (
      <li key={item.href}>
        <TooltipRail colapsado={colapsado} label={label}>
          {link}
        </TooltipRail>
      </li>
    );
  }

  return (
    <nav
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        // Fondo OPACO en móvil (el drawer es un overlay sobre el contenido · con
        // `bg-muted/40` translúcido el texto del menú se encimaba con la página) y
        // el tinte sutil de la barra lateral solo en desktop.
        "multiva-navbar fixed left-0 flex flex-col overflow-visible border-r border-border max-md:bg-background md:bg-muted/40 transition-transform duration-200 ease-out-quint",
        // Sin prefijo de breakpoint, `w-62` quedaba activo TAMBIÉN en desktop y
        // empataba en especificidad con `md:w-[...]` (mismo peso, decide orden
        // de salida del CSS generado) · escopar ambas a su breakpoint con
        // `max-md:`/`md:` las vuelve mutuamente excluyentes, sin ambigüedad.
        "max-md:w-62 md:w-[var(--shell-navbar-w)]",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}
      style={{
        top: "var(--shell-header-h)",
        height: "calc(100dvh - var(--shell-header-h))",
        zIndex: Z_INDEX.appNavbar,
      }}
      aria-label="Navegación principal"
    >
      <div className="flex-1 overflow-y-auto px-2 py-3">
        {/* Los headers de sección NO se desmontan al colapsar · su altura se anima a 0
            (ver SectionLabel/GrupoLabel) para que los items no "salten" al ocultarse. */}
        <SectionLabel colapsado={colapsado}>Navegación</SectionLabel>
        <ul className="mb-4 flex flex-col gap-0.5">{principal.map(renderItem)}</ul>

        {hayAdmin && (
          <>
            <SectionLabel colapsado={colapsado}>Administración</SectionLabel>
            {adminGrupos.map((grupo, i) => (
              <div key={grupo.titulo}>
                <GrupoLabel colapsado={colapsado} className={i > 0 ? "mt-3" : undefined}>
                  {grupo.titulo}
                </GrupoLabel>
                <ul className="flex flex-col gap-0.5">{grupo.items.map(renderItem)}</ul>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="space-y-2.5 border-t border-border px-2 py-2.5">
        {/* Customize Layout · abre el modal de visibilidad de widgets del dashboard. */}
        <button
          type="button"
          onClick={() => setCustomizeOpen(true)}
          title="Customize Layout"
          className={cn(
            // Alineación izquierda constante (igual que los items) · el icono no se
            // desplaza al colapsar y queda alineado con los del menú en el rail.
            "flex w-full items-center gap-2.5 overflow-hidden rounded-xl border border-border bg-card px-2.5 py-2 text-xs font-semibold text-foreground shadow-2xs transition-colors hover:bg-muted"
          )}
        >
          <IconAdjustmentsHorizontal size={15} className="shrink-0 text-muted-foreground" />
          <span
            className={cn(
              "block truncate transition-[max-width,opacity] ease-[cubic-bezier(0.08,1,0.12,1)] duration-[var(--navbar-anim-ms,720ms)]",
              colapsado ? "max-w-0 opacity-0" : "max-w-[140px] opacity-100"
            )}
          >
            Customize Layout
          </span>
        </button>

        {colapsado ? (
          <div className="flex justify-center">
            <Badge size="xs" variant="light" color="multiva">
              v2.0
            </Badge>
          </div>
        ) : (
          <div className="flex items-center justify-between px-1 text-[11px] font-medium text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-exito-500" aria-hidden="true" />
              Portal
            </span>
            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-secondary-foreground">v2.0</span>
          </div>
        )}
      </div>

      <CustomizeLayoutModal open={customizeOpen} onClose={() => setCustomizeOpen(false)} />
    </nav>
  );
}

/**
 * Sub-encabezado de una subcategoría del menú de administración · más discreto
 * que `SectionLabel` (normal-case y más tenue) para marcar la jerarquía
 * ADMINISTRACIÓN › grupo.
 */
function GrupoLabel({
  children,
  className,
  colapsado,
}: {
  children: ReactNode;
  className?: string;
  colapsado?: boolean;
}) {
  return (
    <p
      aria-hidden={colapsado || undefined}
      className={cn(
        // Altura y margen RESERVADOS siempre (solo fade de opacidad) · así los
        // iconos no se mueven verticalmente al colapsar/expandir.
        "mb-1 max-h-5 overflow-hidden px-2.5 text-[11px] font-medium text-muted-foreground/70",
        "transition-opacity ease-[cubic-bezier(0.08,1,0.12,1)] duration-[var(--navbar-anim-ms,720ms)]",
        colapsado ? "opacity-0" : "opacity-100",
        className
      )}
    >
      {children}
    </p>
  );
}

/**
 * Tooltip lateral de un item del menú · SOLO visible en modo rail (colapsado).
 *
 * Bug corregido: el Tooltip se monta siempre (estructura estable, ver arriba) pero en
 * modo expandido no renderiza su contenido. Radix cierra un tooltip "hoverable" al salir
 * del trigger calculando el área de gracia hacia el CONTENIDO; sin contenido montado ese
 * cierre nunca ocurre, así que cada item por el que pasaba el mouse con el menú expandido
 * quedaba `open` internamente y, al contraer, TODOS los tooltips aparecían a la vez.
 * Solución: estado controlado · expandido → nunca se abre (y se fuerza cerrado).
 */
function TooltipRail({ colapsado, label, children }: { colapsado: boolean; label: ReactNode; children: ReactNode }) {
  const [abierto, setAbierto] = useState(false);
  // El menú se expande por hover: si un tooltip estaba abierto al expandirse, su estado
  // interno quedaría en true y reaparecería al contraer · se reinicia al expandir.
  useEffect(() => {
    if (!colapsado) setAbierto(false);
  }, [colapsado]);
  return (
    <Tooltip open={colapsado && abierto} onOpenChange={(o) => setAbierto(colapsado ? o : false)}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {colapsado && <TooltipContent side="right">{label}</TooltipContent>}
    </Tooltip>
  );
}
